"use client";

import { useState, useEffect } from "react";
import type { CampsiteStoryblok } from "@/component-types-sb";
import { clsx } from "clsx";

import { FilterSkeleton } from "@/components/layout/Skeleton";
import campsitesJson from "@/lib/campsites.json";

type FilterProps = {
  onSelect?: (value: CampsiteStoryblok | null) => void;
  type?: "events" | "news" | "faq" | "offers";
};

const countableTypes = ["news", "offers"] as const;

const isCountableType = (
  value?: FilterProps["type"]
): value is (typeof countableTypes)[number] =>
  !!value && countableTypes.includes(value as (typeof countableTypes)[number]);

const eventCampsites = campsitesJson as unknown as CampsiteStoryblok[];

const Filter = ({ onSelect, type }: FilterProps) => {
  const [campsites, setCampsites] = useState<CampsiteStoryblok[]>(
    type === "events" ? eventCampsites : []
  );
  const [selectedCampsite, setSelectedCampsite] =
    useState<CampsiteStoryblok | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (type === "events") {
      setCampsites(eventCampsites);
      setIsLoading(false);
      return;
    }

    const fetchData = async () => {
      setIsLoading(true);

      try {
        const countType = isCountableType(type) ? type : null;
        const res = await fetch(
          `/api/campsites${countType ? `?counts=${countType}` : ""}`
        );
        const json = await res.json();
        const campsites = Array.isArray(json) ? json : [];
        const data =
          countType
            ? campsites.filter(
                (campsite: CampsiteStoryblok) => campsite[countType] > 0
              )
            : campsites;
        setCampsites(data);
      } catch (error) {
        console.error(error);
        setCampsites([]);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [type]);

  const handleClick = (value: CampsiteStoryblok | null) => {
    setSelectedCampsite(value);
    onSelect?.(value);
  };

  return (
    <div className="snap-x scroll-px-4 snap-mandatory scroll-smooth flex md:flex-wrap justify-left md:justify-center gap-3 mx-4 mb-8 pb-4 md:pb-0 overflow-x-auto">
      <button
        className={clsx(
          "bg-white snap-start shrink-0 py-1 px-2 border text-sm hover:border-brand-primary100 hover:text-brand-primary100",
          {
            ["border-brand-primary100 text-brand-primary100 font-medium"]:
              !selectedCampsite,
            ["border-gray-400"]: selectedCampsite,
          }
        )}
        onClick={() => {
          handleClick(null);
        }}
      >
        Alle Campingparks
      </button>

      {isLoading && <FilterSkeleton number={4} />}

      {campsites?.map((campsite, index: number) => (
        <button
          key={index}
          className={clsx(
            "bg-white snap-start shrink-0 py-1 px-2 border text-sm hover:border-brand-primary100 hover:text-brand-primary100 shadow-sm",
            {
              ["border-brand-primary100 text-brand-primary100 font-medium"]:
                campsite === selectedCampsite,
              ["border-gray-400"]: campsite !== selectedCampsite,
            }
          )}
          onClick={() => {
            handleClick(campsite);
          }}
        >
          {campsite.name}
        </button>
      ))}
    </div>
  );
};

export { Filter };
